import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { Flex, Spin } from "@murm/ant";
import SecureRoute from "../components/auth/SecureRoute";
import { LandingPage } from "../pages/search/LandingPage";
import Login from "../pages/auth/Login";
import Logout from "../components/auth/Logout";
import { HealthPage } from "../pages/HealthPage";
import { ErrorPage } from "../pages/ErrorPage";
import ChunkErrorBoundary, { clearChunkReloadLatch } from "./ChunkErrorBoundary";
import styles from "./Fallback.module.css";

/**
 * Split out of the initial bundle, one chunk per route.
 *
 * Everything used to compile into a single 1.57 MB (gzipped) file that every visitor downloaded
 * before anything rendered, including mapbox-gl -- which only /search-results uses -- and the chart
 * libraries, which the landing page and login do not touch. Splitting takes the initial download to
 * 612 kB and hands each route its own chunk on demand.
 *
 * Left eager on purpose: the landing page and login are the first thing most sessions render, and
 * /health is a probe endpoint that should never wait on a second request.
 */
/**
 * `lazy`, plus clearing the reload latch on success.
 *
 * The latch has to be released *here* and nowhere earlier. Clearing it when the app mounts would
 * look equivalent and is not: after a genuinely broken deploy the eager bundle still mounts fine,
 * so the latch would clear, the next chunk 404 would reload, and the tab would loop forever.
 * Releasing it only when a chunk has actually arrived means one retry per broken deploy, and a
 * later deploy still gets its own.
 */
const lazyRoute = (load) =>
  lazy(() =>
    load().then((module) => {
      clearChunkReloadLatch();
      return module;
    })
  );

const SearchResults = lazyRoute(() => import("../pages/search/SearchResults"));
const ScrollableLayout = lazyRoute(() => import("../pages/district/ScrollableLayout"));
const PerformancePage = lazyRoute(() =>
  import("../pages/PerformancePage").then((m) => ({ default: m.PerformancePage }))
);
const EarlyVotePage = lazyRoute(() =>
  import("../pages/earlyVote/EarlyVotePage").then((m) => ({ default: m.EarlyVotePage }))
);
const HelpCenter = lazyRoute(() =>
  import("../pages/helpCenter/HelpCenter").then((m) => ({ default: m.HelpCenter }))
);

/** Matches SecureRoute's own pending state, so a lazy route and an auth check look the same. */
const RouteFallback = () => (
  <Flex align="center" justify="center" className={styles.fullScreen}>
    <Spin size="large" />
  </Flex>
);

function AppRoutes() {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
      <Route path="/health" element={<HealthPage />} />
      <Route
        path="/performance"
        element={
          <SecureRoute>
            <PerformancePage />
          </SecureRoute>
        }
      />
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <SecureRoute>
            <LandingPage />
          </SecureRoute>
        }
      />
      <Route
        path="/error"
        element={
          <SecureRoute>
            <ErrorPage />
          </SecureRoute>
        }
      />
      <Route
        path="/search-results"
        element={
          <SecureRoute>
            <SearchResults />
          </SecureRoute>
        }
      />
      <Route
        path="/district-profile"
        element={
          <SecureRoute>
            <ScrollableLayout />
          </SecureRoute>
        }
      />
      <Route
        path="/early-voting"
        element={
          <SecureRoute>
            <EarlyVotePage />
          </SecureRoute>
        }
      />
      <Route
        path="/help-center"
        element={
          <SecureRoute>
            <HelpCenter />
          </SecureRoute>
        }
      />
      <Route path="/logout" element={<Logout />} />
        </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export default AppRoutes;
